import { useState } from 'react';
import { UnitInvitation, UserRole } from '../../types';
import { MembershipRequest } from '../../hooks/useUnitMembershipRequests';
import { UserPlus, Mail, X, UserCheck, UserX } from 'lucide-react';

interface InvitationsTabProps {
  canEdit: boolean;
  invitations: UnitInvitation[];
  membershipRequests: MembershipRequest[];
  processingRequestId: string | null;
  sendingInvite: boolean;
  handleSendInvitation: (email: string, role: UserRole) => Promise<boolean>;
  handleRevokeInvitation: (invitationId: string) => Promise<boolean>;
  handleApproveRequest: (request: MembershipRequest) => Promise<boolean>;
  handleRejectRequest: (request: MembershipRequest) => Promise<boolean>;
  confirmAction: (options: any) => Promise<any>;
  formatDate: (date: Date) => string;
  Card: any;
  CardContent: any;
  CardDescription: any;
  CardHeader: any;
  CardTitle: any;
  Button: any;
  Input: any;
  Label: any;
  Select: any;
  SelectTrigger: any;
  SelectContent: any;
  SelectItem: any;
  SelectValue: any;
  Badge: any;
}

export function InvitationsTab({
  canEdit,
  invitations,
  membershipRequests,
  processingRequestId,
  sendingInvite,
  handleSendInvitation,
  handleRevokeInvitation,
  handleApproveRequest,
  handleRejectRequest,
  confirmAction,
  formatDate,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Badge,
}: InvitationsTabProps) {
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<UserRole>('Member');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await handleSendInvitation(inviteEmail, inviteRole);
    if (success) {
      setInviteEmail('');
      setInviteRole('Member');
    }
  };

  const onRevoke = async (invitationId: string) => {
    const result = await confirmAction({
      title: 'Revoke Invitation?',
      text: 'This will cancel the pending invitation.',
    });

    if (result.isConfirmed) {
      await handleRevokeInvitation(invitationId);
    }
  };

  const onApproveRequest = async (request: MembershipRequest) => {
    const result = await confirmAction({
      title: 'Approve Membership Request?',
      text: `Add ${request.userEmail} as a member of your unit?`,
      confirmButtonText: 'Approve',
    });

    if (result.isConfirmed) {
      await handleApproveRequest(request);
    }
  };

  const onRejectRequest = async (request: MembershipRequest) => {
    const result = await confirmAction({
      title: 'Reject Membership Request?',
      text: `Decline the membership request from ${request.userEmail}?`,
      confirmButtonText: 'Reject',
      confirmButtonColor: '#dc2626',
    });

    if (result.isConfirmed) {
      await handleRejectRequest(request);
    }
  };

  return (
    <div className="space-y-6">
      {canEdit && (
        <Card>
          <CardHeader>
            <CardTitle>Send Invitation</CardTitle>
            <CardDescription>Invite new members to join your unit</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="inviteEmail">Email Address *</Label>
                  <Input
                    id="inviteEmail"
                    type="email"
                    placeholder="member@example.com"
                    value={inviteEmail}
                    onChange={(e: any) => setInviteEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inviteRole">Role *</Label>
                  <Select
                    value={inviteRole}
                    onValueChange={(value: UserRole) => setInviteRole(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Guest">Guest</SelectItem>
                      <SelectItem value="Member">Member</SelectItem>
                      <SelectItem value="Steward">Steward</SelectItem>
                      <SelectItem value="Treasurer">Treasurer</SelectItem>
                      <SelectItem value="Secretary">Secretary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="submit" disabled={sendingInvite}>
                <UserPlus className="mr-2 h-4 w-4" />
                {sendingInvite ? 'Sending...' : 'Send Invitation'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {canEdit && (
        <Card>
          <CardHeader>
            <CardTitle>Membership Requests</CardTitle>
            <CardDescription>Members who have requested to join your unit</CardDescription>
          </CardHeader>
          <CardContent>
            {membershipRequests.length > 0 ? (
              <div className="space-y-2">
                {membershipRequests.map((request) => (
                  <div
                    key={request.id}
                    className="flex items-center justify-between rounded-lg border p-4"
                  >
                    <div className="flex-1">
                      <div className="font-semibold">{request.userEmail}</div>
                      <div className="text-sm text-muted-foreground">
                        Requested on {formatDate(request.createdAt)}
                      </div>
                      {request.requestType === 'direct_request' && (
                        <div className="text-xs text-muted-foreground mt-1">
                          Join request
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => onApproveRequest(request)}
                        disabled={processingRequestId === request.id}
                      >
                        <UserCheck className="mr-1 h-4 w-4" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onRejectRequest(request)}
                        disabled={processingRequestId === request.id}
                      >
                        <UserX className="mr-1 h-4 w-4" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No pending membership requests
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Pending Invitations</CardTitle>
          <CardDescription>Invitations waiting to be accepted</CardDescription>
        </CardHeader>
        <CardContent>
          {invitations.length > 0 ? (
            <div className="space-y-2">
              {invitations.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between rounded-lg border p-4"
                >
                  <div className="flex-1">
                    <div className="font-semibold">{invite.email}</div>
                    <div className="text-sm text-muted-foreground">
                      Invited by {invite.invitedByName} on {formatDate(invite.createdAt)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Expires on {formatDate(invite.expiresAt)}
                    </div>
                    {invite.userExists === false && (
                      <div className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        User needs to create an account first
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge>{invite.role}</Badge>
                    {invite.userExists === false && (
                      <Badge
                        variant="warning"
                        className="bg-amber-100 text-amber-800 border-amber-200"
                      >
                        Pending Registration
                      </Badge>
                    )}
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onRevoke(invite.id)}
                        title="Revoke invitation"
                      >
                        <X className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No pending invitations
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

